import Dexie from "dexie";

class MedicalDatabase extends Dexie {
  constructor() {
    super("MedicalRecordsDB");

    // Version 1 with old schema (for reference)
    this.version(1).stores({
      patients: "++id, name, age, gender, status, createdAt, updatedAt",
      visits:
        "++id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription",
      medicalDataset: "++id, category, name, isCustom, createdAt",
    });

    // Version 2 with UUID string keys
    this.version(2)
      .stores({
        patients: "id, name, age, gender, status, createdAt, updatedAt",
        visits:
          "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription",
        medicalDataset: "++id, category, name, isCustom, createdAt",
      })
      .upgrade((tx) => {
        // Clear existing data that uses integer IDs to avoid conflicts
        return tx
          .table("patients")
          .clear()
          .then(() => {
            return tx.table("visits").clear();
          });
      });

    // Version 3 with compound index for duplicate checking
    this.version(3).stores({
      patients: "id, name, age, gender, status, createdAt, updatedAt",
      visits:
        "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription",
      medicalDataset:
        "++id, category, name, isCustom, createdAt, [category+name]",
    });

    // Version 4 with administered medications
    this.version(4).stores({
      patients: "id, name, age, gender, status, createdAt, updatedAt",
      visits:
        "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription, administeredMeds",
      medicalDataset:
        "++id, category, name, isCustom, createdAt, [category+name]",
    });

    // Version 5 with visit drafts/templates
    this.version(5).stores({
      patients: "id, name, age, gender, status, createdAt, updatedAt",
      visits:
        "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription, administeredMeds",
      medicalDataset:
        "++id, category, name, isCustom, createdAt, [category+name]",
      drafts:
        "id, name, description, category, template, isPublic, usageCount, createdBy, createdAt, updatedAt, [category+name]",
    });

    // Version 6 with voice commands and doctor memory
    this.version(6).stores({
      patients: "id, name, age, gender, status, createdAt, updatedAt",
      visits:
        "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription, administeredMeds",
      medicalDataset:
        "++id, category, name, isCustom, createdAt, [category+name]",
      drafts:
        "id, name, description, category, template, isPublic, usageCount, createdBy, createdAt, updatedAt, [category+name]",
      voiceCommands:
        "id, doctorId, name, type, triggers, actions, parameters, isActive, usageCount, createdAt, updatedAt, [doctorId+name]",
      doctorMemory:
        "id, doctorId, category, title, content, isActive, priority, createdAt, updatedAt, [doctorId+category]",
    });

    // Version 7 with appointments
    this.version(7).stores({
      patients: "id, name, age, gender, status, createdAt, updatedAt",
      visits:
        "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription, administeredMeds",
      medicalDataset:
        "++id, category, name, isCustom, createdAt, [category+name]",
      drafts:
        "id, name, description, category, template, isPublic, usageCount, createdBy, createdAt, updatedAt, [category+name]",
      voiceCommands:
        "id, doctorId, name, type, triggers, actions, parameters, isActive, usageCount, createdAt, updatedAt, [doctorId+name]",
      doctorMemory:
        "id, doctorId, category, title, content, isActive, priority, createdAt, updatedAt, [doctorId+category]",
      appointments:
        "id, patientId, doctorId, date, startTime, endTime, type, status, notes, createdAt, updatedAt, [patientId+date], [doctorId+date]",
    });

    // Version 8 with billing system
    this.version(8).stores({
      patients: "id, name, age, gender, status, createdAt, updatedAt",
      visits:
        "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription, administeredMeds, visitTypeId, performedActions, billingStatus",
      medicalDataset:
        "++id, category, name, isCustom, createdAt, [category+name]",
      drafts:
        "id, name, description, category, template, isPublic, usageCount, createdBy, createdAt, updatedAt, [category+name]",
      voiceCommands:
        "id, doctorId, name, type, triggers, actions, parameters, isActive, usageCount, createdAt, updatedAt, [doctorId+name]",
      doctorMemory:
        "id, doctorId, category, title, content, isActive, priority, createdAt, updatedAt, [doctorId+category]",
      appointments:
        "id, patientId, doctorId, date, startTime, endTime, type, status, notes, createdAt, updatedAt, [patientId+date], [doctorId+date]",
      visitTypes:
        "++id, name, defaultPrice, description, isActive, createdAt, updatedAt",
      medicalActions:
        "++id, name, defaultPrice, category, description, isActive, createdAt, updatedAt, [category+name]",
      transactions:
        "id, patientId, visitId, date, items, totalAmount, paidAmount, paymentMethod, status, receiptNumber, notes, createdAt, updatedAt, [patientId+date], [status+date]",
      medicationPricing:
        "++id, medicationName, defaultPrice, category, lastUpdated, [medicationName+category]",
    });

    // Version 9 with audit logging (same schema, just to ensure audit table is created)
    this.version(9).stores({
      patients: "id, name, age, gender, status, createdAt, updatedAt",
      visits:
        "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription, administeredMeds, visitTypeId, performedActions, billingStatus",
      medicalDataset:
        "++id, category, name, isCustom, createdAt, [category+name]",
      drafts:
        "id, name, description, category, template, isPublic, usageCount, createdBy, createdAt, updatedAt, [category+name]",
      voiceCommands:
        "id, doctorId, name, type, triggers, actions, parameters, isActive, usageCount, createdAt, updatedAt, [doctorId+name]",
      doctorMemory:
        "id, doctorId, category, title, content, isActive, priority, createdAt, updatedAt, [doctorId+category]",
      appointments:
        "id, patientId, doctorId, date, startTime, endTime, type, status, notes, createdAt, updatedAt, [patientId+date], [doctorId+date]",
      visitTypes:
        "++id, name, defaultPrice, description, isActive, createdAt, updatedAt",
      medicalActions:
        "++id, name, defaultPrice, category, description, isActive, createdAt, updatedAt, [category+name]",
      transactions:
        "id, patientId, visitId, date, items, totalAmount, paidAmount, paymentMethod, status, receiptNumber, notes, createdAt, updatedAt, [patientId+date], [status+date]",
      medicationPricing:
        "++id, medicationName, defaultPrice, category, lastUpdated, [medicationName+category]",
      auditLog:
        "++id, entityType, entityId, action, oldData, newData, userId, timestamp, ipAddress, [entityType+entityId]",
    });

    // Version 10 with additional patient fields (phone, location, bloodType)
    this.version(10).stores({
      patients: "id, name, age, gender, status, phone, location, bloodType, createdAt, updatedAt",
      visits:
        "id, patientId, date, complaints, vitalSigns, allergies, infections, pastHistory, notes, takenDrugs, prescription, administeredMeds, visitTypeId, performedActions, billingStatus",
      medicalDataset:
        "++id, category, name, isCustom, createdAt, [category+name]",
      drafts:
        "id, name, description, category, template, isPublic, usageCount, createdBy, createdAt, updatedAt, [category+name]",
      voiceCommands:
        "id, doctorId, name, type, triggers, actions, parameters, isActive, usageCount, createdAt, updatedAt, [doctorId+name]",
      doctorMemory:
        "id, doctorId, category, title, content, isActive, priority, createdAt, updatedAt, [doctorId+category]",
      appointments:
        "id, patientId, doctorId, date, startTime, endTime, type, status, notes, createdAt, updatedAt, [patientId+date], [doctorId+date]",
      visitTypes:
        "++id, name, defaultPrice, description, isActive, createdAt, updatedAt",
      medicalActions:
        "++id, name, defaultPrice, category, description, isActive, createdAt, updatedAt, [category+name]",
      transactions:
        "id, patientId, visitId, date, items, totalAmount, paidAmount, paymentMethod, status, receiptNumber, notes, createdAt, updatedAt, [patientId+date], [status+date]",
      medicationPricing:
        "++id, medicationName, defaultPrice, category, lastUpdated, [medicationName+category]",
      auditLog:
        "++id, entityType, entityId, action, oldData, newData, userId, timestamp, ipAddress, [entityType+entityId]",
    });

    // Add hooks for automatic timestamps
    this.patients.hook("creating", (primKey, obj, trans) => {
      obj.id = this.generateUUID();
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
    });

    this.patients.hook("updating", (modifications, primKey, obj, trans) => {
      modifications.updatedAt = new Date();
    });

    this.visits.hook("creating", (primKey, obj, trans) => {
      obj.id = this.generateUUID();
      obj.date = obj.date || new Date();
    });

    this.medicalDataset.hook("creating", (primKey, obj, trans) => {
      obj.createdAt = new Date();
    });

    this.drafts.hook("creating", (primKey, obj, trans) => {
      obj.id = this.generateUUID();
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
      obj.usageCount = obj.usageCount || 0;
      obj.isPublic = obj.isPublic || false;
      obj.createdBy = obj.createdBy || "default";
    });

    this.drafts.hook("updating", (modifications, primKey, obj, trans) => {
      modifications.updatedAt = new Date();
    });

    // Voice Commands hooks
    this.voiceCommands.hook("creating", (primKey, obj, trans) => {
      obj.id = this.generateUUID();
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
      obj.usageCount = obj.usageCount || 0;
      obj.isActive = obj.isActive !== false; // Default to true
    });

    this.voiceCommands.hook(
      "updating",
      (modifications, primKey, obj, trans) => {
        modifications.updatedAt = new Date();
      }
    );

    // Doctor Memory hooks
    this.doctorMemory.hook("creating", (primKey, obj, trans) => {
      obj.id = this.generateUUID();
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
      obj.isActive = obj.isActive !== false; // Default to true
      obj.priority = obj.priority || 1; // Default priority
    });

    this.doctorMemory.hook("updating", (modifications, primKey, obj, trans) => {
      modifications.updatedAt = new Date();
    });

    // Appointments hooks
    this.appointments.hook("creating", (primKey, obj, trans) => {
      obj.id = this.generateUUID();
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
      obj.doctorId = obj.doctorId || "default-doctor"; // Default doctor for single-doctor setup
      obj.status = obj.status || "scheduled"; // Default status
    });

    this.appointments.hook("updating", (modifications, primKey, obj, trans) => {
      modifications.updatedAt = new Date();
    });

    // Billing system hooks
    this.visitTypes.hook("creating", (primKey, obj, trans) => {
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
      obj.isActive = obj.isActive !== false; // Default to true
    });

    this.visitTypes.hook("updating", (modifications, primKey, obj, trans) => {
      modifications.updatedAt = new Date();
    });

    this.medicalActions.hook("creating", (primKey, obj, trans) => {
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
      obj.isActive = obj.isActive !== false; // Default to true
    });

    this.medicalActions.hook(
      "updating",
      (modifications, primKey, obj, trans) => {
        modifications.updatedAt = new Date();
      }
    );

    this.transactions.hook("creating", (primKey, obj, trans) => {
      obj.id = this.generateUUID();
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
      obj.date = obj.date || new Date();
      obj.status = obj.status || "pending";
    });

    this.transactions.hook("updating", (modifications, primKey, obj, trans) => {
      modifications.updatedAt = new Date();
    });

    this.medicationPricing.hook("creating", (primKey, obj, trans) => {
      obj.lastUpdated = new Date();
    });

    this.medicationPricing.hook(
      "updating",
      (modifications, primKey, obj, trans) => {
        modifications.lastUpdated = new Date();
      }
    );

    // Open database and seed initial data
    this.open()
      .then(() => {
        this.seedMedicalDataset();
        this.seedInitialDrafts();
        this.seedBillingData();
      })
      .catch((error) => {
        console.error("Failed to open database:", error);
      });
  }

  generateUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  // Appointment Constants
  static APPOINTMENT_TYPES = {
    ROUTINE: { name: "Routine Checkup", duration: 30 },
    FOLLOW_UP: { name: "Follow-up", duration: 20 },
    NEW_PATIENT: { name: "New Patient", duration: 45 },
    CONSULTATION: { name: "Consultation", duration: 30 },
    EMERGENCY: { name: "Emergency", duration: 15 },
  };

  static APPOINTMENT_STATUS = {
    SCHEDULED: "scheduled",
    COMPLETED: "completed",
    CANCELLED: "cancelled",
  };

  // Billing Constants
  static PAYMENT_STATUS = {
    PAID: "paid",
    PARTIAL: "partial",
    PENDING: "pending",
    OVERDUE: "overdue",
  };

  static PAYMENT_METHODS = {
    CASH: "cash",
    CARD: "card",
    CHECK: "check",
    BANK_TRANSFER: "bank_transfer",
  };

  static BILLING_STATUS = {
    PENDING: "pending",
    BILLED: "billed",
    PAID: "paid",
    PARTIAL: "partial",
  };

  static MEDICAL_ACTION_CATEGORIES = {
    DIAGNOSTIC: "diagnostic",
    THERAPEUTIC: "therapeutic",
    PREVENTIVE: "preventive",
    ADMINISTRATIVE: "administrative",
  };

  // Patient Management
  async createPatient(patientData) {
    try {
      const id = await this.patients.add({
        name: patientData.name,
        age: parseInt(patientData.age),
        gender: patientData.gender,
        status: patientData.status || "Under Observation",
        phone: patientData.phone || "",
        location: patientData.location || "",
        bloodType: patientData.bloodType || "",
      });
      return await this.getPatient(id);
    } catch (error) {
      console.error("Error creating patient:", error);
      throw error;
    }
  }

  async getPatient(id) {
    try {
      const patient = await this.patients.get(id);
      if (patient) {
        patient.visits = await this.visits
          .where("patientId")
          .equals(id)
          .toArray();
      }
      return patient;
    } catch (error) {
      console.error("Error getting patient:", error);
      throw error;
    }
  }

  async updatePatient(id, updates) {
    try {
      await this.patients.update(id, updates);
      return await this.getPatient(id);
    } catch (error) {
      console.error("Error updating patient:", error);
      throw error;
    }
  }

  async getUniqueLocations() {
    try {
      const patients = await this.patients.toArray();
      const locations = patients
        .map((p) => p.location)
        .filter((loc) => loc && loc.trim() !== "");
      // Return unique locations
      return [...new Set(locations)].sort();
    } catch (error) {
      console.error("Error getting unique locations:", error);
      return [];
    }
  }

  async getAllPatients(filters = {}) {
    try {
      let query = this.patients.toCollection();

      // Apply filters
      if (filters.search) {
        query = query.filter((patient) =>
          patient.name.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      if (filters.status) {
        query = query.filter((patient) => patient.status === filters.status);
      }

      // Apply sorting
      let patients = await query.toArray();

      if (filters.sortBy) {
        patients.sort((a, b) => {
          switch (filters.sortBy) {
            case "name":
              return a.name.localeCompare(b.name);
            case "age":
              return a.age - b.age;
            case "updated":
            default:
              return new Date(b.updatedAt) - new Date(a.updatedAt);
          }
        });
      }

      // Get visit counts and appointment info for each patient
      for (let patient of patients) {
        patient.visitCount = await this.visits
          .where("patientId")
          .equals(patient.id)
          .count();

        // Get the most recent visit for this patient
        const patientVisits = await this.visits
          .where("patientId")
          .equals(patient.id)
          .toArray();

        if (patientVisits.length > 0) {
          // Sort visits by date in descending order and get the first one
          patientVisits.sort((a, b) => new Date(b.date) - new Date(a.date));
          patient.lastVisit = patientVisits[0];
        } else {
          patient.lastVisit = null;
        }

        // Get next upcoming appointment for this patient
        const upcomingAppointments = await this.appointments
          .where("patientId")
          .equals(patient.id)
          .toArray();

        if (upcomingAppointments.length > 0) {
          // Filter for future appointments and sort by date/time
          const futureAppointments = upcomingAppointments
            .filter((apt) => {
              const aptDateTime = new Date(apt.date + " " + apt.startTime);
              return aptDateTime > new Date() && apt.status === "scheduled";
            })
            .sort((a, b) => {
              const dateTimeA = new Date(a.date + " " + a.startTime);
              const dateTimeB = new Date(b.date + " " + b.startTime);
              return dateTimeA - dateTimeB;
            });

          patient.nextAppointment = futureAppointments[0] || null;
        } else {
          patient.nextAppointment = null;
        }
      }

      return patients;
    } catch (error) {
      console.error("Error getting all patients:", error);
      throw error;
    }
  }

  // Visit Management
  async createVisit(patientId, visitData) {
    try {
      const id = await this.visits.add({
        patientId: patientId,
        complaints: visitData.complaints || [],
        vitalSigns: visitData.vitalSigns || {},
        allergies: visitData.allergies || [],
        infections: visitData.infections || [],
        pastHistory: visitData.pastHistory || [],
        notes: visitData.notes || "",
        takenDrugs: visitData.takenDrugs || [],
        prescription: visitData.prescription || [],
        administeredMeds: visitData.administeredMeds || [],
      });

      // Update patient's updatedAt timestamp
      await this.patients.update(patientId, { updatedAt: new Date() });

      return await this.visits.get(id);
    } catch (error) {
      console.error("Error creating visit:", error);
      throw error;
    }
  }

  async updateVisit(id, updates) {
    try {
      const visit = await this.visits.get(id);
      if (visit) {
        await this.visits.update(id, updates);
        // Update patient's updatedAt timestamp
        await this.patients.update(visit.patientId, { updatedAt: new Date() });
      }
      return await this.visits.get(id);
    } catch (error) {
      console.error("Error updating visit:", error);
      throw error;
    }
  }

  async deleteVisit(id) {
    try {
      const visit = await this.visits.get(id);
      if (visit) {
        await this.visits.delete(id);
        // Update patient's updatedAt timestamp
        await this.patients.update(visit.patientId, { updatedAt: new Date() });
      }
    } catch (error) {
      console.error("Error deleting visit:", error);
      throw error;
    }
  }

  async getPatientVisits(patientId) {
    try {
      return await this.visits
        .where("patientId")
        .equals(patientId)
        .toArray()
        .then((visits) => {
          // Sort visits by date in descending order (most recent first)
          return visits.sort((a, b) => new Date(b.date) - new Date(a.date));
        });
    } catch (error) {
      console.error("Error getting patient visits:", error);
      throw error;
    }
  }

  // Appointment Management
  async createAppointment(appointmentData) {
    try {
      const id = await this.appointments.add({
        patientId: appointmentData.patientId,
        doctorId: appointmentData.doctorId || "default-doctor",
        date: appointmentData.date,
        startTime: appointmentData.startTime,
        endTime: appointmentData.endTime,
        type: appointmentData.type,
        status: appointmentData.status || "scheduled",
        notes: appointmentData.notes || "",
      });

      // Update patient's updatedAt timestamp
      await this.patients.update(appointmentData.patientId, {
        updatedAt: new Date(),
      });

      return await this.getAppointment(id);
    } catch (error) {
      console.error("Error creating appointment:", error);
      throw error;
    }
  }

  async getAppointment(id) {
    try {
      const appointment = await this.appointments.get(id);
      if (appointment) {
        // Get patient information
        appointment.patient = await this.patients.get(appointment.patientId);
      }
      return appointment;
    } catch (error) {
      console.error("Error getting appointment:", error);
      throw error;
    }
  }

  async updateAppointment(id, updates) {
    try {
      await this.appointments.update(id, updates);
      return await this.getAppointment(id);
    } catch (error) {
      console.error("Error updating appointment:", error);
      throw error;
    }
  }

  async deleteAppointment(id) {
    try {
      await this.appointments.delete(id);
    } catch (error) {
      console.error("Error deleting appointment:", error);
      throw error;
    }
  }

  async getAllAppointments(filters = {}) {
    try {
      let query = this.appointments.toCollection();

      // Apply filters
      if (filters.patientId) {
        query = query.filter((apt) => apt.patientId === filters.patientId);
      }

      if (filters.doctorId) {
        query = query.filter((apt) => apt.doctorId === filters.doctorId);
      }

      if (filters.status) {
        query = query.filter((apt) => apt.status === filters.status);
      }

      if (filters.date) {
        const filterDate = new Date(filters.date).toDateString();
        query = query.filter(
          (apt) => new Date(apt.date).toDateString() === filterDate
        );
      }

      // Apply sorting
      let appointments = await query.toArray();

      // Sort by date and start time
      appointments.sort((a, b) => {
        const dateA = new Date(a.date + " " + a.startTime);
        const dateB = new Date(b.date + " " + b.startTime);
        return dateA - dateB;
      });

      // Get patient information for each appointment
      for (let appointment of appointments) {
        appointment.patient = await this.patients.get(appointment.patientId);
      }

      return appointments;
    } catch (error) {
      console.error("Error getting all appointments:", error);
      throw error;
    }
  }

  async getAppointmentsByDate(date) {
    try {
      const targetDate = new Date(date).toDateString();
      const appointments = await this.appointments
        .toCollection()
        .filter((apt) => new Date(apt.date).toDateString() === targetDate)
        .toArray();

      // Sort by start time
      appointments.sort((a, b) => {
        const timeA = new Date("1970-01-01 " + a.startTime);
        const timeB = new Date("1970-01-01 " + b.startTime);
        return timeA - timeB;
      });

      // Get patient information for each appointment
      for (let appointment of appointments) {
        appointment.patient = await this.patients.get(appointment.patientId);
      }

      return appointments;
    } catch (error) {
      console.error("Error getting appointments by date:", error);
      throw error;
    }
  }

  async getPatientAppointments(patientId) {
    try {
      const appointments = await this.appointments
        .where("patientId")
        .equals(patientId)
        .toArray();

      // Sort by date and time (most recent first)
      appointments.sort((a, b) => {
        const dateTimeA = new Date(a.date + " " + a.startTime);
        const dateTimeB = new Date(b.date + " " + b.startTime);
        return dateTimeB - dateTimeA;
      });

      return appointments;
    } catch (error) {
      console.error("Error getting patient appointments:", error);
      throw error;
    }
  }

  async getTodaysAppointments(doctorId = "default-doctor") {
    try {
      const today = new Date().toDateString();
      return await this.getAppointmentsByDate(today);
    } catch (error) {
      console.error("Error getting today's appointments:", error);
      throw error;
    }
  }

  async getTomorrowsAppointments(doctorId = "default-doctor") {
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return await this.getAppointmentsByDate(tomorrow);
    } catch (error) {
      console.error("Error getting tomorrow's appointments:", error);
      throw error;
    }
  }

  // Check for appointment time conflicts
  async checkAppointmentConflicts(appointmentData, excludeId = null) {
    try {
      const {
        date,
        startTime,
        endTime,
        doctorId = "default-doctor",
      } = appointmentData;

      // Get all appointments for the same date and doctor
      const dayAppointments = await this.appointments
        .where("date")
        .equals(date)
        .and((apt) => apt.doctorId === doctorId && apt.status === "scheduled")
        .toArray();

      // Exclude the current appointment if editing
      const relevantAppointments = excludeId
        ? dayAppointments.filter((apt) => apt.id !== excludeId)
        : dayAppointments;

      // Convert time strings to minutes for easier comparison
      const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(":").map(Number);
        return hours * 60 + minutes;
      };

      const newStart = timeToMinutes(startTime);
      const newEnd = timeToMinutes(endTime);

      // Check for overlaps
      const conflicts = relevantAppointments.filter((apt) => {
        const existingStart = timeToMinutes(apt.startTime);
        const existingEnd = timeToMinutes(apt.endTime);

        // Check if times overlap (including buffer time)
        const bufferMinutes = 5; // 5-minute buffer between appointments
        return (
          newStart < existingEnd + bufferMinutes &&
          newEnd > existingStart - bufferMinutes
        );
      });

      return conflicts;
    } catch (error) {
      console.error("Error checking appointment conflicts:", error);
      return [];
    }
  }

  // Get suggested alternative time slots
  async getSuggestedTimeSlots(
    date,
    duration = 30,
    doctorId = "default-doctor"
  ) {
    try {
      const dayAppointments = await this.appointments
        .where("date")
        .equals(date)
        .and((apt) => apt.doctorId === doctorId && apt.status === "scheduled")
        .toArray();

      // Standard working hours (8 AM to 6 PM)
      const workingHours = {
        start: 8 * 60, // 8:00 AM in minutes
        end: 18 * 60, // 6:00 PM in minutes
      };

      const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(":").map(Number);
        return hours * 60 + minutes;
      };

      const minutesToTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, "0")}:${mins
          .toString()
          .padStart(2, "0")}`;
      };

      // Get occupied time slots
      const occupiedSlots = dayAppointments
        .map((apt) => ({
          start: timeToMinutes(apt.startTime),
          end: timeToMinutes(apt.endTime),
        }))
        .sort((a, b) => a.start - b.start);

      // Find available slots
      const availableSlots = [];
      let currentTime = workingHours.start;

      for (const slot of occupiedSlots) {
        // Check if there's space before this appointment
        if (currentTime + duration + 5 <= slot.start) {
          // 5-minute buffer
          availableSlots.push({
            startTime: minutesToTime(currentTime),
            endTime: minutesToTime(currentTime + duration),
          });
        }
        currentTime = Math.max(currentTime, slot.end + 5); // Move past this appointment with buffer
      }

      // Check if there's space after the last appointment
      if (currentTime + duration <= workingHours.end) {
        availableSlots.push({
          startTime: minutesToTime(currentTime),
          endTime: minutesToTime(currentTime + duration),
        });
      }

      // If no appointments exist, suggest standard slots
      if (occupiedSlots.length === 0) {
        for (
          let time = workingHours.start;
          time + duration <= workingHours.end;
          time += 30
        ) {
          availableSlots.push({
            startTime: minutesToTime(time),
            endTime: minutesToTime(time + duration),
          });
        }
      }

      return availableSlots.slice(0, 6); // Return up to 6 suggestions
    } catch (error) {
      console.error("Error getting suggested time slots:", error);
      return [];
    }
  }

  // Medical Dataset Management
  async seedMedicalDataset() {
    try {
      const count = await this.medicalDataset.count();
      if (count > 0) return; // Already seeded

      const medicalData = {
        symptoms: [
          "Fever",
          "Headache",
          "Nausea",
          "Vomiting",
          "Diarrhea",
          "Constipation",
          "Abdominal pain",
          "Back pain",
          "Chest pain",
          "Joint pain",
          "Muscle pain",
          "Shortness of breath",
          "Cough",
          "Sore throat",
          "Runny nose",
          "Fatigue",
          "Dizziness",
          "Weakness",
          "Loss of appetite",
          "Weight loss",
          "Weight gain",
          "Rash",
          "Itching",
          "Swelling",
          "Palpitations",
          "Insomnia",
          "Anxiety",
          "Depression",
          "Confusion",
          "Memory loss",
          "Blurred vision",
        ],
        drugs: [
          "Paracetamol",
          "Acetaminophen",
          "Ibuprofen",
          "Aspirin",
          "Diclofenac",
          "Omeprazole",
          "Ranitidine",
          "Metformin",
          "Insulin",
          "Lisinopril",
          "Amlodipine",
          "Atorvastatin",
          "Simvastatin",
          "Warfarin",
          "Clopidogrel",
          "Amoxicillin",
          "Azithromycin",
          "Ciprofloxacin",
          "Doxycycline",
          "Penicillin",
        ],
        diagnoses: [
          "Hypertension",
          "Diabetes mellitus",
          "Hyperlipidemia",
          "Obesity",
          "Coronary artery disease",
          "Heart failure",
          "Atrial fibrillation",
          "Asthma",
          "COPD",
          "Pneumonia",
          "Bronchitis",
          "Upper respiratory infection",
          "Gastroesophageal reflux disease",
          "Peptic ulcer disease",
          "Gastritis",
        ],
        allergens: [
          "Penicillin",
          "Sulfa drugs",
          "Aspirin",
          "NSAIDs",
          "Latex",
          "Peanuts",
          "Tree nuts",
          "Shellfish",
          "Fish",
          "Eggs",
          "Milk",
          "Soy",
          "Wheat",
          "Pollen",
          "Dust mites",
          "Pet dander",
          "Mold",
          "Bee stings",
          "Wasp stings",
        ],
        routes: [
          "PO (Oral)",
          "IV (Intravenous)",
          "IM (Intramuscular)",
          "SC (Subcutaneous)",
          "Topical",
          "Inhaled",
          "Sublingual",
          "Rectal",
        ],
        "administered-medications": [
          "Morphine",
          "Epinephrine",
          "Insulin",
          "Saline",
          "Dextrose",
          "Lidocaine",
          "Atropine",
          "Diphenhydramine",
          "Furosemide",
          "Metoclopramide",
          "Ondansetron",
          "Ketorolac",
          "Hydrocortisone",
          "Methylprednisolone",
        ],
      };

      const items = [];
      for (const [category, names] of Object.entries(medicalData)) {
        for (const name of names) {
          items.push({
            category: category,
            name: name,
            isCustom: false,
          });
        }
      }

      await this.medicalDataset.bulkAdd(items);
      console.log("Medical dataset seeded successfully");
    } catch (error) {
      console.error("Error seeding medical dataset:", error);
    }
  }

  async getMedicalDataset(category = null) {
    try {
      let query = this.medicalDataset.toCollection();
      if (category) {
        query = query.filter((item) => item.category === category);
      }
      return await query.sortBy("name");
    } catch (error) {
      console.error("Error getting medical dataset:", error);
      throw error;
    }
  }

  async addMedicalDatasetItem(category, name) {
    try {
      // Check if item already exists
      const existing = await this.medicalDataset
        .where(["category", "name"])
        .equals([category, name])
        .first();

      if (existing) {
        throw new Error("Item already exists");
      }

      const id = await this.medicalDataset.add({
        category: category,
        name: name,
        isCustom: true,
      });

      return await this.medicalDataset.get(id);
    } catch (error) {
      console.error("Error adding medical dataset item:", error);
      throw error;
    }
  }

  async deleteMedicalDatasetItem(id) {
    try {
      await this.medicalDataset.delete(id);
    } catch (error) {
      console.error("Error deleting medical dataset item:", error);
      throw error;
    }
  }

  // Data Export/Import
  async exportData() {
    try {
      const patients = await this.patients.toArray();
      const visits = await this.visits.toArray();
      const medicalDataset = await this.medicalDataset
        .where("isCustom")
        .equals(true)
        .toArray();

      // Organize visits by patient
      for (const patient of patients) {
        patient.visits = visits.filter((v) => v.patientId === patient.id);
      }

      return {
        patients: patients,
        customMedicalDataset: medicalDataset,
        exportedAt: new Date().toISOString(),
        version: "1.0",
      };
    } catch (error) {
      console.error("Error exporting data:", error);
      throw error;
    }
  }

  // Draft Management
  async createDraft(draftData) {
    try {
      const id = await this.drafts.add({
        name: draftData.name,
        description: draftData.description || "",
        category: draftData.category || "General",
        template: draftData.template || {},
        isPublic: draftData.isPublic || false,
        createdBy: draftData.createdBy || "default",
      });
      return await this.getDraft(id);
    } catch (error) {
      console.error("Error creating draft:", error);
      throw error;
    }
  }

  async getDraft(id) {
    try {
      return await this.drafts.get(id);
    } catch (error) {
      console.error("Error getting draft:", error);
      throw error;
    }
  }

  async updateDraft(id, updates) {
    try {
      await this.drafts.update(id, updates);
      return await this.getDraft(id);
    } catch (error) {
      console.error("Error updating draft:", error);
      throw error;
    }
  }

  async deleteDraft(id) {
    try {
      await this.drafts.delete(id);
    } catch (error) {
      console.error("Error deleting draft:", error);
      throw error;
    }
  }

  async getAllDrafts(filters = {}) {
    try {
      let query = this.drafts.toCollection();

      // Apply filters
      if (filters.search) {
        query = query.filter(
          (draft) =>
            draft.name.toLowerCase().includes(filters.search.toLowerCase()) ||
            draft.description
              .toLowerCase()
              .includes(filters.search.toLowerCase())
        );
      }

      if (filters.category) {
        query = query.filter((draft) => draft.category === filters.category);
      }

      // Apply sorting
      let drafts = await query.toArray();

      if (filters.sortBy) {
        drafts.sort((a, b) => {
          switch (filters.sortBy) {
            case "name":
              return a.name.localeCompare(b.name);
            case "category":
              return a.category.localeCompare(b.category);
            case "usage":
              return b.usageCount - a.usageCount; // Most used first
            case "created":
              return new Date(b.createdAt) - new Date(a.createdAt);
            case "updated":
            default:
              return new Date(b.updatedAt) - new Date(a.updatedAt);
          }
        });
      } else {
        // Default sort by usage count (most popular first), then by update date
        drafts.sort((a, b) => {
          if (b.usageCount === a.usageCount) {
            return new Date(b.updatedAt) - new Date(a.updatedAt);
          }
          return b.usageCount - a.usageCount;
        });
      }

      return drafts;
    } catch (error) {
      console.error("Error getting all drafts:", error);
      throw error;
    }
  }

  async getPopularDrafts(limit = 4) {
    try {
      const drafts = await this.drafts
        .orderBy("usageCount")
        .reverse()
        .limit(limit)
        .toArray();
      return drafts;
    } catch (error) {
      console.error("Error getting popular drafts:", error);
      throw error;
    }
  }

  async getRecentDrafts(limit = 4) {
    try {
      const drafts = await this.drafts
        .orderBy("updatedAt")
        .reverse()
        .limit(limit)
        .toArray();
      return drafts;
    } catch (error) {
      console.error("Error getting recent drafts:", error);
      throw error;
    }
  }

  async incrementDraftUsage(id) {
    try {
      const draft = await this.getDraft(id);
      if (draft) {
        await this.updateDraft(id, {
          usageCount: (draft.usageCount || 0) + 1,
        });
      }
    } catch (error) {
      console.error("Error incrementing draft usage:", error);
      throw error;
    }
  }

  async getDraftsByCategory(category) {
    try {
      return await this.drafts
        .where("category")
        .equals(category)
        .sortBy("name");
    } catch (error) {
      console.error("Error getting drafts by category:", error);
      throw error;
    }
  }

  async createDraftFromVisit(
    visitData,
    draftName,
    draftDescription = "",
    category = "General"
  ) {
    try {
      // Extract template from visit data, excluding visit-specific fields
      const template = {
        complaints: visitData.complaints || [],
        vitalSigns: {
          temp: null, // Don't copy specific vital signs, keep structure
          bp: null,
          hr: null,
          spo2: null,
          weight: null,
        },
        allergies: visitData.allergies || [],
        infections: visitData.infections || [],
        pastHistory: visitData.pastHistory || [],
        notes: visitData.notes || "",
        takenDrugs: visitData.takenDrugs || [],
        prescription: visitData.prescription || [],
        administeredMeds: visitData.administeredMeds || [],
      };

      const draftData = {
        name: draftName,
        description: draftDescription,
        category: category,
        template: template,
      };

      return await this.createDraft(draftData);
    } catch (error) {
      console.error("Error creating draft from visit:", error);
      throw error;
    }
  }

  async seedInitialDrafts() {
    try {
      const count = await this.drafts.count();
      if (count > 0) return; // Already seeded

      const initialDrafts = [
        {
          name: "Routine Checkup",
          description: "Standard annual physical examination",
          category: "Preventive Care",
          template: {
            complaints: ["Annual physical", "Routine checkup"],
            vitalSigns: {
              temp: null,
              bp: null,
              hr: null,
              spo2: null,
              weight: null,
            },
            allergies: [],
            infections: [],
            pastHistory: [],
            notes: "Annual physical examination - all systems reviewed",
            takenDrugs: [],
            prescription: [],
            administeredMeds: [],
          },
        },
        {
          name: "Common Cold",
          description: "Typical upper respiratory infection visit",
          category: "Acute Care",
          template: {
            complaints: ["Runny nose", "Sore throat", "Cough", "Fatigue"],
            vitalSigns: {
              temp: null,
              bp: null,
              hr: null,
              spo2: null,
              weight: null,
            },
            allergies: [],
            infections: ["Upper respiratory infection"],
            pastHistory: [],
            notes: "Patient presenting with cold symptoms",
            takenDrugs: [],
            prescription: [
              {
                name: "Paracetamol",
                dose: "500",
                unit: "mg",
                duration: "5 days as needed",
              },
            ],
            administeredMeds: [],
          },
        },
        {
          name: "Hypertension Follow-up",
          description: "Regular blood pressure monitoring visit",
          category: "Chronic Care",
          template: {
            complaints: ["Follow-up for hypertension"],
            vitalSigns: {
              temp: null,
              bp: null,
              hr: null,
              spo2: null,
              weight: null,
            },
            allergies: [],
            infections: [],
            pastHistory: ["Hypertension"],
            notes:
              "Hypertension follow-up visit - medication compliance reviewed",
            takenDrugs: [{ name: "Lisinopril", dose: "10", unit: "mg" }],
            prescription: [],
            administeredMeds: [],
          },
        },
        {
          name: "Diabetes Management",
          description: "Routine diabetes monitoring and management",
          category: "Chronic Care",
          template: {
            complaints: ["Diabetes follow-up"],
            vitalSigns: {
              temp: null,
              bp: null,
              hr: null,
              spo2: null,
              weight: null,
            },
            allergies: [],
            infections: [],
            pastHistory: ["Diabetes mellitus"],
            notes: "Diabetes management visit - blood sugar levels reviewed",
            takenDrugs: [{ name: "Metformin", dose: "500", unit: "mg" }],
            prescription: [],
            administeredMeds: [],
          },
        },
        {
          name: "Flu Symptoms",
          description: "Influenza-like illness visit",
          category: "Acute Care",
          template: {
            complaints: ["Fever", "Body aches", "Fatigue", "Headache"],
            vitalSigns: {
              temp: null,
              bp: null,
              hr: null,
              spo2: null,
              weight: null,
            },
            allergies: [],
            infections: [],
            pastHistory: [],
            notes: "Patient presenting with flu-like symptoms",
            takenDrugs: [],
            prescription: [
              {
                name: "Paracetamol",
                dose: "500",
                unit: "mg",
                duration: "7 days as needed",
              },
              {
                name: "Ibuprofen",
                dose: "400",
                unit: "mg",
                duration: "5 days as needed",
              },
            ],
            administeredMeds: [],
          },
        },
      ];

      await this.drafts.bulkAdd(initialDrafts);
      console.log("Initial drafts seeded successfully");
    } catch (error) {
      console.error("Error seeding initial drafts:", error);
    }
  }

  // Voice Commands Management
  async createVoiceCommand(commandData) {
    try {
      const id = await this.voiceCommands.add({
        doctorId: commandData.doctorId,
        name: commandData.name,
        type: commandData.type, // 'simple', 'sequence', 'parameterized', 'natural'
        triggers: commandData.triggers || [], // Array of trigger phrases
        actions: commandData.actions || [], // Array of action objects
        parameters: commandData.parameters || {}, // Parameter definitions
        isActive: commandData.isActive !== false,
      });
      return await this.getVoiceCommand(id);
    } catch (error) {
      console.error("Error creating voice command:", error);
      throw error;
    }
  }

  async getVoiceCommand(id) {
    try {
      return await this.voiceCommands.get(id);
    } catch (error) {
      console.error("Error getting voice command:", error);
      throw error;
    }
  }

  async updateVoiceCommand(id, updates) {
    try {
      await this.voiceCommands.update(id, updates);
      return await this.getVoiceCommand(id);
    } catch (error) {
      console.error("Error updating voice command:", error);
      throw error;
    }
  }

  async deleteVoiceCommand(id) {
    try {
      await this.voiceCommands.delete(id);
    } catch (error) {
      console.error("Error deleting voice command:", error);
      throw error;
    }
  }

  async getDoctorVoiceCommands(doctorId, filters = {}) {
    try {
      let query = this.voiceCommands.where("doctorId").equals(doctorId);

      if (filters.activeOnly !== false) {
        query = query.filter((cmd) => cmd.isActive);
      }

      if (filters.type) {
        query = query.filter((cmd) => cmd.type === filters.type);
      }

      let commands = await query.toArray();

      // Sort by usage count (most used first), then by name
      commands.sort((a, b) => {
        if (b.usageCount === a.usageCount) {
          return a.name.localeCompare(b.name);
        }
        return b.usageCount - a.usageCount;
      });

      return commands;
    } catch (error) {
      console.error("Error getting doctor voice commands:", error);
      throw error;
    }
  }

  async incrementVoiceCommandUsage(id) {
    try {
      const command = await this.getVoiceCommand(id);
      if (command) {
        await this.updateVoiceCommand(id, {
          usageCount: (command.usageCount || 0) + 1,
        });
      }
    } catch (error) {
      console.error("Error incrementing voice command usage:", error);
      throw error;
    }
  }

  async findMatchingVoiceCommands(doctorId, inputText) {
    try {
      const commands = await this.getDoctorVoiceCommands(doctorId, {
        activeOnly: true,
      });
      const matches = [];

      for (const command of commands) {
        for (const trigger of command.triggers) {
          if (this.matchesTrigger(inputText, trigger)) {
            matches.push({
              command,
              confidence: this.calculateMatchConfidence(inputText, trigger),
              matchedTrigger: trigger,
            });
            break; // Only add each command once
          }
        }
      }

      // Sort by confidence score (highest first)
      matches.sort((a, b) => b.confidence - a.confidence);

      return matches;
    } catch (error) {
      console.error("Error finding matching voice commands:", error);
      throw error;
    }
  }

  // Helper method to check if input matches a trigger
  matchesTrigger(input, trigger) {
    const inputLower = input.toLowerCase().trim();
    const triggerLower = trigger.toLowerCase().trim();

    // Exact match
    if (inputLower === triggerLower) return true;

    // Contains match
    if (inputLower.includes(triggerLower)) return true;

    // Word boundary match
    const words = inputLower.split(/\s+/);
    const triggerWords = triggerLower.split(/\s+/);

    // Check if all trigger words are present
    return triggerWords.every((word) =>
      words.some((inputWord) => inputWord.includes(word))
    );
  }

  // Helper method to calculate match confidence
  calculateMatchConfidence(input, trigger) {
    const inputLower = input.toLowerCase().trim();
    const triggerLower = trigger.toLowerCase().trim();

    // Exact match = 100%
    if (inputLower === triggerLower) return 1.0;

    // Calculate similarity based on word overlap
    const inputWords = new Set(inputLower.split(/\s+/));
    const triggerWords = new Set(triggerLower.split(/\s+/));

    const intersection = new Set(
      [...inputWords].filter((x) => triggerWords.has(x))
    );
    const union = new Set([...inputWords, ...triggerWords]);

    return intersection.size / union.size;
  }

  // Doctor Memory Management
  async createDoctorMemory(memoryData) {
    try {
      const id = await this.doctorMemory.add({
        doctorId: memoryData.doctorId,
        category: memoryData.category, // 'guidelines', 'preferences', 'protocols', 'notes'
        title: memoryData.title,
        content: memoryData.content,
        isActive: memoryData.isActive !== false,
        priority: memoryData.priority || 1, // 1-5, higher = more important
      });
      return await this.getDoctorMemory(id);
    } catch (error) {
      console.error("Error creating doctor memory:", error);
      throw error;
    }
  }

  async getDoctorMemory(id) {
    try {
      return await this.doctorMemory.get(id);
    } catch (error) {
      console.error("Error getting doctor memory:", error);
      throw error;
    }
  }

  async updateDoctorMemory(id, updates) {
    try {
      await this.doctorMemory.update(id, updates);
      return await this.getDoctorMemory(id);
    } catch (error) {
      console.error("Error updating doctor memory:", error);
      throw error;
    }
  }

  async deleteDoctorMemory(id) {
    try {
      await this.doctorMemory.delete(id);
    } catch (error) {
      console.error("Error deleting doctor memory:", error);
      throw error;
    }
  }

  async getDoctorMemoryByCategory(doctorId, category, activeOnly = true) {
    try {
      let query = this.doctorMemory
        .where("[doctorId+category]")
        .equals([doctorId, category]);

      if (activeOnly) {
        query = query.filter((memory) => memory.isActive);
      }

      const memories = await query.toArray();

      // Sort by priority (highest first), then by creation date
      memories.sort((a, b) => {
        if (b.priority === a.priority) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        }
        return b.priority - a.priority;
      });

      return memories;
    } catch (error) {
      console.error("Error getting doctor memory by category:", error);
      throw error;
    }
  }

  async getAllDoctorMemory(doctorId, activeOnly = true) {
    try {
      let query = this.doctorMemory.where("doctorId").equals(doctorId);

      if (activeOnly) {
        query = query.filter((memory) => memory.isActive);
      }

      const memories = await query.toArray();

      // Sort by priority (highest first), then by category, then by creation date
      memories.sort((a, b) => {
        if (b.priority === a.priority) {
          if (a.category === b.category) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return a.category.localeCompare(b.category);
        }
        return b.priority - a.priority;
      });

      return memories;
    } catch (error) {
      console.error("Error getting all doctor memory:", error);
      throw error;
    }
  }

  async seedDoctorMemory(doctorId) {
    try {
      // Check if doctor already has memory entries
      const existingMemory = await this.getAllDoctorMemory(doctorId);
      if (existingMemory.length > 0) return;

      const defaultMemory = [
        {
          doctorId: doctorId,
          category: "guidelines",
          title: "HIPAA Compliance",
          content:
            "Always maintain patient confidentiality. Never share patient information without proper authorization. Ensure all communications are secure.",
          priority: 5,
        },
        {
          doctorId: doctorId,
          category: "guidelines",
          title: "Medical Documentation",
          content:
            "Document all patient interactions thoroughly. Include symptoms, vital signs, assessments, and treatment plans. Use clear, professional language.",
          priority: 4,
        },
        {
          doctorId: doctorId,
          category: "preferences",
          title: "Voice Command Style",
          content:
            "Prefer concise confirmations for routine tasks. Always ask for confirmation before making significant changes to patient records.",
          priority: 3,
        },
        {
          doctorId: doctorId,
          category: "protocols",
          title: "Emergency Procedures",
          content:
            "For emergency situations, prioritize immediate care documentation. Voice commands should focus on critical vitals and interventions.",
          priority: 5,
        },
      ];

      await this.doctorMemory.bulkAdd(defaultMemory);
      console.log("Default doctor memory seeded for:", doctorId);
    } catch (error) {
      console.error("Error seeding doctor memory:", error);
      throw error;
    }
  }

  async seedDefaultVoiceCommands(doctorId) {
    try {
      // Check if doctor already has voice commands
      const existingCommands = await this.getDoctorVoiceCommands(doctorId);
      if (existingCommands.length > 0) return;

      const defaultCommands = [
        {
          doctorId: doctorId,
          name: "Add Patient",
          type: "simple",
          triggers: ["add patient", "new patient", "create patient"],
          actions: ["add-patient"],
          parameters: {},
          isActive: true,
        },
        {
          doctorId: doctorId,
          name: "View Patients",
          type: "simple",
          triggers: ["view patients", "show patients", "patient list"],
          actions: ["view-patients"],
          parameters: {},
          isActive: true,
        },
        {
          doctorId: doctorId,
          name: "View Dashboard",
          type: "simple",
          triggers: ["view dashboard", "show dashboard", "dashboard"],
          actions: ["view-dashboard"],
          parameters: {},
          isActive: true,
        },
        {
          doctorId: doctorId,
          name: "Export Data",
          type: "simple",
          triggers: ["export data", "export", "download data"],
          actions: ["export-data"],
          parameters: {},
          isActive: true,
        },
      ];

      await this.voiceCommands.bulkAdd(defaultCommands);
      console.log("Default voice commands seeded for:", doctorId);
    } catch (error) {
      console.error("Error seeding default voice commands:", error);
    }
  }

  // ===== BILLING SYSTEM METHODS =====

  // Visit Types Management
  async createVisitType(visitTypeData) {
    try {
      const id = await this.visitTypes.add({
        name: visitTypeData.name,
        defaultPrice: parseFloat(visitTypeData.defaultPrice),
        description: visitTypeData.description || "",
        isActive: visitTypeData.isActive !== false,
      });
      return await this.getVisitType(id);
    } catch (error) {
      console.error("Error creating visit type:", error);
      throw error;
    }
  }

  async getVisitType(id) {
    try {
      return await this.visitTypes.get(id);
    } catch (error) {
      console.error("Error getting visit type:", error);
      throw error;
    }
  }

  async updateVisitType(id, updates) {
    try {
      await this.visitTypes.update(id, updates);
      return await this.getVisitType(id);
    } catch (error) {
      console.error("Error updating visit type:", error);
      throw error;
    }
  }

  async getAllVisitTypes(activeOnly = true) {
    try {
      let query = this.visitTypes.toCollection();
      if (activeOnly) {
        query = query.filter((vt) => vt.isActive);
      }
      return await query.sortBy("name");
    } catch (error) {
      console.error("Error getting visit types:", error);
      throw error;
    }
  }

  // Medical Actions Management
  async createMedicalAction(actionData) {
    try {
      const id = await this.medicalActions.add({
        name: actionData.name,
        defaultPrice: parseFloat(actionData.defaultPrice),
        category: actionData.category,
        description: actionData.description || "",
        isActive: actionData.isActive !== false,
      });
      return await this.getMedicalAction(id);
    } catch (error) {
      console.error("Error creating medical action:", error);
      throw error;
    }
  }

  async getMedicalAction(id) {
    try {
      return await this.medicalActions.get(id);
    } catch (error) {
      console.error("Error getting medical action:", error);
      throw error;
    }
  }

  async updateMedicalAction(id, updates) {
    try {
      await this.medicalActions.update(id, updates);
      return await this.getMedicalAction(id);
    } catch (error) {
      console.error("Error updating medical action:", error);
      throw error;
    }
  }

  async getAllMedicalActions(filters = {}) {
    try {
      let query = this.medicalActions.toCollection();

      if (filters.activeOnly !== false) {
        query = query.filter((action) => action.isActive);
      }

      if (filters.category) {
        query = query.filter((action) => action.category === filters.category);
      }

      return await query.sortBy("name");
    } catch (error) {
      console.error("Error getting medical actions:", error);
      throw error;
    }
  }

  async getMedicalActionsByCategory(category, activeOnly = true) {
    try {
      let query = this.medicalActions.where("category").equals(category);
      if (activeOnly) {
        query = query.filter((action) => action.isActive);
      }
      return await query.sortBy("name");
    } catch (error) {
      console.error("Error getting medical actions by category:", error);
      throw error;
    }
  }

  // Medication Pricing Management
  async setMedicationPrice(medicationName, price, category = "general") {
    try {
      // Check if pricing already exists
      const existing = await this.medicationPricing
        .where("[medicationName+category]")
        .equals([medicationName, category])
        .first();

      if (existing) {
        await this.medicationPricing.update(existing.id, {
          defaultPrice: parseFloat(price),
        });
        return await this.medicationPricing.get(existing.id);
      } else {
        const id = await this.medicationPricing.add({
          medicationName,
          defaultPrice: parseFloat(price),
          category,
        });
        return await this.medicationPricing.get(id);
      }
    } catch (error) {
      console.error("Error setting medication price:", error);
      throw error;
    }
  }

  async getMedicationPrice(medicationName, category = "general") {
    try {
      const pricing = await this.medicationPricing
        .where("[medicationName+category]")
        .equals([medicationName, category])
        .first();
      return pricing ? pricing.defaultPrice : 0;
    } catch (error) {
      console.error("Error getting medication price:", error);
      return 0;
    }
  }

  async getAllMedicationPricing() {
    try {
      return await this.medicationPricing.orderBy("medicationName").toArray();
    } catch (error) {
      console.error("Error getting all medication pricing:", error);
      throw error;
    }
  }

  // Transaction Management
  async createTransaction(transactionData) {
    try {
      // Generate receipt number
      const receiptNumber = await this.generateReceiptNumber();

      const id = await this.transactions.add({
        patientId: transactionData.patientId,
        visitId: transactionData.visitId || null,
        date: transactionData.date || new Date(),
        items: transactionData.items || [],
        totalAmount: parseFloat(transactionData.totalAmount),
        paidAmount: parseFloat(transactionData.paidAmount),
        paymentMethod: transactionData.paymentMethod,
        status: this.calculatePaymentStatus(
          transactionData.totalAmount,
          transactionData.paidAmount
        ),
        receiptNumber,
        notes: transactionData.notes || "",
      });

      // Update visit billing status if visitId provided
      if (transactionData.visitId) {
        const billingStatus = this.calculatePaymentStatus(
          transactionData.totalAmount,
          transactionData.paidAmount
        );
        await this.visits.update(transactionData.visitId, { billingStatus });
      }

      // Return the created transaction with populated data
      if (id) {
        return await this.getTransaction(id);
      } else {
        throw new Error("Failed to create transaction - no ID returned");
      }
    } catch (error) {
      console.error("Error creating transaction:", error);
      throw error;
    }
  }

  async getTransaction(id) {
    try {
      if (!id) {
        return null;
      }

      const transaction = await this.transactions.get(id);
      if (transaction) {
        // Get patient information
        if (transaction.patientId) {
          try {
            transaction.patient = await this.patients.get(
              transaction.patientId
            );
          } catch (error) {
            console.warn(`Patient not found for transaction ${id}:`, error);
            transaction.patient = null;
          }
        }
        // Get visit information if available
        if (transaction.visitId) {
          try {
            transaction.visit = await this.visits.get(transaction.visitId);
          } catch (error) {
            console.warn(`Visit not found for transaction ${id}:`, error);
            transaction.visit = null;
          }
        }
      }
      return transaction;
    } catch (error) {
      console.error("Error getting transaction:", error);
      return null; // Return null instead of throwing to prevent cascading errors
    }
  }

  async updateTransaction(id, updates) {
    try {
      // Recalculate status if amounts changed
      if (
        updates.totalAmount !== undefined ||
        updates.paidAmount !== undefined
      ) {
        const transaction = await this.transactions.get(id);
        const totalAmount =
          updates.totalAmount !== undefined
            ? updates.totalAmount
            : transaction.totalAmount;
        const paidAmount =
          updates.paidAmount !== undefined
            ? updates.paidAmount
            : transaction.paidAmount;
        updates.status = this.calculatePaymentStatus(totalAmount, paidAmount);
      }

      await this.transactions.update(id, updates);
      return await this.getTransaction(id);
    } catch (error) {
      console.error("Error updating transaction:", error);
      throw error;
    }
  }

  async getAllTransactions(filters = {}) {
    try {
      // Check if transactions table exists and has data
      const transactionCount = await this.transactions.count();
      if (transactionCount === 0) {
        return []; // Return empty array if no transactions exist
      }

      let query = this.transactions.toCollection();

      // Apply filters
      if (filters.patientId) {
        query = query.filter((t) => t.patientId === filters.patientId);
      }

      if (filters.status) {
        query = query.filter((t) => t.status === filters.status);
      }

      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        query = query.filter((t) => new Date(t.date) >= fromDate);
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        query = query.filter((t) => new Date(t.date) <= toDate);
      }

      let transactions = await query.toArray();

      // Sort by date (most recent first)
      transactions.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Get patient information for each transaction
      for (let transaction of transactions) {
        if (transaction.patientId) {
          try {
            transaction.patient = await this.patients.get(
              transaction.patientId
            );
          } catch (error) {
            console.warn(
              `Patient not found for transaction ${transaction.id}:`,
              error
            );
            transaction.patient = null;
          }
        }
        if (transaction.visitId) {
          try {
            transaction.visit = await this.visits.get(transaction.visitId);
          } catch (error) {
            console.warn(
              `Visit not found for transaction ${transaction.id}:`,
              error
            );
            transaction.visit = null;
          }
        }
      }

      return transactions;
    } catch (error) {
      console.error("Error getting all transactions:", error);
      return []; // Return empty array on error to prevent UI crashes
    }
  }

  async getPatientTransactions(patientId) {
    try {
      if (!patientId) {
        return [];
      }

      const transactions = await this.transactions
        .where("patientId")
        .equals(patientId)
        .toArray();

      // Sort by date (most recent first)
      transactions.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Get visit information for each transaction
      for (let transaction of transactions) {
        if (transaction.visitId) {
          try {
            transaction.visit = await this.visits.get(transaction.visitId);
          } catch (error) {
            console.warn(
              `Visit not found for transaction ${transaction.id}:`,
              error
            );
            transaction.visit = null;
          }
        }
      }

      return transactions;
    } catch (error) {
      console.error("Error getting patient transactions:", error);
      return []; // Return empty array on error
    }
  }

  async getPatientBalance(patientId) {
    try {
      const transactions = await this.getPatientTransactions(patientId);
      let totalOwed = 0;
      let totalPaid = 0;

      for (const transaction of transactions) {
        totalOwed += transaction.totalAmount;
        totalPaid += transaction.paidAmount;
      }

      return {
        totalOwed,
        totalPaid,
        balance: totalOwed - totalPaid,
        transactions: transactions.length,
      };
    } catch (error) {
      console.error("Error getting patient balance:", error);
      throw error;
    }
  }

  // Billing Calculations and Utilities
  calculatePaymentStatus(totalAmount, paidAmount) {
    const total = parseFloat(totalAmount);
    const paid = parseFloat(paidAmount);

    if (paid >= total) {
      return this.constructor.PAYMENT_STATUS.PAID;
    } else if (paid > 0) {
      return this.constructor.PAYMENT_STATUS.PARTIAL;
    } else {
      return this.constructor.PAYMENT_STATUS.PENDING;
    }
  }

  async generateReceiptNumber() {
    try {
      const today = new Date();
      const dateStr = today.toISOString().slice(0, 10).replace(/-/g, "");

      // Get today's transaction count
      const todayTransactions = await this.transactions
        .where("date")
        .between(
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
          new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            23,
            59,
            59
          )
        )
        .count();

      const sequence = (todayTransactions + 1).toString().padStart(4, "0");
      return `RCP-${dateStr}-${sequence}`;
    } catch (error) {
      console.error("Error generating receipt number:", error);
      // Fallback to timestamp-based number
      return `RCP-${Date.now()}`;
    }
  }

  async calculateVisitBilling(visitData, visitTypeId, performedActions = []) {
    try {
      const items = [];
      let totalAmount = 0;

      // Add visit type cost
      if (visitTypeId) {
        const visitType = await this.getVisitType(visitTypeId);
        if (visitType) {
          items.push({
            type: "visit",
            id: visitType.id,
            name: visitType.name,
            price: visitType.defaultPrice,
            quantity: 1,
          });
          totalAmount += visitType.defaultPrice;
        }
      }

      // Add performed actions costs
      for (const performedAction of performedActions) {
        if (performedAction && performedAction.actionId) {
          const action = await this.getMedicalAction(performedAction.actionId);
          if (action) {
            // Use custom price if provided, otherwise use default price
            const price =
              performedAction.customPrice &&
              parseFloat(performedAction.customPrice) > 0
                ? parseFloat(performedAction.customPrice)
                : action.defaultPrice;

            items.push({
              type: "action",
              id: action.id,
              name: action.name,
              price: price,
              quantity: 1,
            });
            totalAmount += price;
          }
        }
      }

      // Add administered medications costs
      if (visitData.administeredMeds) {
        for (const med of visitData.administeredMeds) {
          const price = await this.getMedicationPrice(med.name);
          items.push({
            type: "medication",
            name: med.name,
            dose: med.dose,
            unit: med.unit,
            price: price,
            quantity: 1,
          });
          totalAmount += price;
        }
      }

      return {
        items,
        totalAmount,
        currency: "ILS",
      };
    } catch (error) {
      console.error("Error calculating visit billing:", error);
      throw error;
    }
  }

  // Financial Reporting
  async getFinancialSummary(dateFrom, dateTo) {
    try {
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);

      const transactions = await this.transactions
        .where("date")
        .between(fromDate, toDate)
        .toArray();

      let totalRevenue = 0;
      let totalPaid = 0;
      let totalPending = 0;
      let transactionCount = transactions.length;

      const statusCounts = {
        paid: 0,
        partial: 0,
        pending: 0,
        overdue: 0,
      };

      for (const transaction of transactions) {
        totalRevenue += transaction.totalAmount;
        totalPaid += transaction.paidAmount;

        if (transaction.status === this.constructor.PAYMENT_STATUS.PENDING) {
          totalPending += transaction.totalAmount;
        }

        statusCounts[transaction.status] =
          (statusCounts[transaction.status] || 0) + 1;
      }

      return {
        period: { from: dateFrom, to: dateTo },
        totalRevenue,
        totalPaid,
        totalPending,
        outstandingBalance: totalRevenue - totalPaid,
        transactionCount,
        statusCounts,
        averageTransactionValue:
          transactionCount > 0 ? totalRevenue / transactionCount : 0,
      };
    } catch (error) {
      console.error("Error getting financial summary:", error);
      throw error;
    }
  }

  async getDailyRevenue(date = new Date()) {
    try {
      const startOfDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
      const endOfDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23,
        59,
        59
      );

      return await this.getFinancialSummary(startOfDay, endOfDay);
    } catch (error) {
      console.error("Error getting daily revenue:", error);
      throw error;
    }
  }

  async getWeeklyRevenue(date = new Date()) {
    try {
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      return await this.getFinancialSummary(startOfWeek, endOfWeek);
    } catch (error) {
      console.error("Error getting weekly revenue:", error);
      throw error;
    }
  }

  async getMonthlyRevenue(date = new Date()) {
    try {
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0,
        23,
        59,
        59
      );

      return await this.getFinancialSummary(startOfMonth, endOfMonth);
    } catch (error) {
      console.error("Error getting monthly revenue:", error);
      throw error;
    }
  }

  async getYearlyRevenue(date = new Date()) {
    try {
      const startOfYear = new Date(date.getFullYear(), 0, 1);
      const endOfYear = new Date(date.getFullYear(), 11, 31, 23, 59, 59);

      return await this.getFinancialSummary(startOfYear, endOfYear);
    } catch (error) {
      console.error("Error getting yearly revenue:", error);
      throw error;
    }
  }

  // Billing Data Seeding
  async seedBillingData() {
    try {
      // Seed visit types
      const visitTypesCount = await this.visitTypes.count();
      if (visitTypesCount === 0) {
        const defaultVisitTypes = [
          {
            name: "Normal Check-up",
            defaultPrice: 100,
            description: "Routine medical examination and consultation",
            isActive: true,
          },
          {
            name: "Follow-up Visit",
            defaultPrice: 80,
            description: "Follow-up appointment for existing condition",
            isActive: true,
          },
          {
            name: "Emergency Consultation",
            defaultPrice: 200,
            description: "Urgent medical consultation",
            isActive: true,
          },
          {
            name: "Specialist Consultation",
            defaultPrice: 150,
            description: "Specialized medical consultation",
            isActive: true,
          },
          {
            name: "Preventive Care",
            defaultPrice: 120,
            description: "Preventive health screening and care",
            isActive: true,
          },
        ];

        await this.visitTypes.bulkAdd(defaultVisitTypes);
        console.log("Visit types seeded successfully");
      }

      // Seed medical actions
      const actionsCount = await this.medicalActions.count();
      if (actionsCount === 0) {
        const defaultActions = [
          // Diagnostic actions
          {
            name: "Blood Test",
            defaultPrice: 50,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.DIAGNOSTIC,
            description: "Complete blood count and basic metabolic panel",
            isActive: true,
          },
          {
            name: "X-Ray",
            defaultPrice: 120,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.DIAGNOSTIC,
            description: "Digital X-ray imaging",
            isActive: true,
          },
          {
            name: "ECG",
            defaultPrice: 80,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.DIAGNOSTIC,
            description: "Electrocardiogram",
            isActive: true,
          },
          {
            name: "Ultrasound",
            defaultPrice: 150,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.DIAGNOSTIC,
            description: "Ultrasound imaging",
            isActive: true,
          },
          // Therapeutic actions
          {
            name: "Injection",
            defaultPrice: 30,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.THERAPEUTIC,
            description: "Intramuscular or intravenous injection",
            isActive: true,
          },
          {
            name: "Wound Dressing",
            defaultPrice: 40,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.THERAPEUTIC,
            description: "Wound cleaning and dressing",
            isActive: true,
          },
          {
            name: "Suturing",
            defaultPrice: 100,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.THERAPEUTIC,
            description: "Wound suturing procedure",
            isActive: true,
          },
          // Preventive actions
          {
            name: "Vaccination",
            defaultPrice: 60,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.PREVENTIVE,
            description: "Immunization vaccination",
            isActive: true,
          },
          {
            name: "Health Screening",
            defaultPrice: 90,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.PREVENTIVE,
            description: "Comprehensive health screening",
            isActive: true,
          },
          // Administrative actions
          {
            name: "Medical Certificate",
            defaultPrice: 25,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.ADMINISTRATIVE,
            description: "Medical fitness certificate",
            isActive: true,
          },
          {
            name: "Prescription Refill",
            defaultPrice: 20,
            category: this.constructor.MEDICAL_ACTION_CATEGORIES.ADMINISTRATIVE,
            description: "Prescription medication refill",
            isActive: true,
          },
        ];

        await this.medicalActions.bulkAdd(defaultActions);
        console.log("Medical actions seeded successfully");
      }

      // Seed medication pricing
      const medicationPricingCount = await this.medicationPricing.count();
      if (medicationPricingCount === 0) {
        const defaultMedicationPricing = [
          {
            medicationName: "Paracetamol",
            defaultPrice: 15,
            category: "general",
          },
          {
            medicationName: "Ibuprofen",
            defaultPrice: 20,
            category: "general",
          },
          { medicationName: "Aspirin", defaultPrice: 18, category: "general" },
          {
            medicationName: "Amoxicillin",
            defaultPrice: 35,
            category: "antibiotic",
          },
          {
            medicationName: "Azithromycin",
            defaultPrice: 45,
            category: "antibiotic",
          },
          {
            medicationName: "Omeprazole",
            defaultPrice: 30,
            category: "gastric",
          },
          { medicationName: "Insulin", defaultPrice: 80, category: "diabetes" },
          {
            medicationName: "Metformin",
            defaultPrice: 25,
            category: "diabetes",
          },
          {
            medicationName: "Lisinopril",
            defaultPrice: 28,
            category: "cardiovascular",
          },
          {
            medicationName: "Amlodipine",
            defaultPrice: 32,
            category: "cardiovascular",
          },
          { medicationName: "Morphine", defaultPrice: 60, category: "pain" },
          {
            medicationName: "Lidocaine",
            defaultPrice: 25,
            category: "anesthetic",
          },
          {
            medicationName: "Epinephrine",
            defaultPrice: 100,
            category: "emergency",
          },
          { medicationName: "Saline", defaultPrice: 10, category: "iv-fluid" },
          {
            medicationName: "Dextrose",
            defaultPrice: 15,
            category: "iv-fluid",
          },
        ];

        await this.medicationPricing.bulkAdd(defaultMedicationPricing);
        console.log("Medication pricing seeded successfully");
      }

      console.log("Billing data seeded successfully");
    } catch (error) {
      console.error("Error seeding billing data:", error);
    }
  }

  // Audit logging methods
  async logAuditEvent(
    entityType,
    entityId,
    action,
    oldData = null,
    newData = null,
    userId = "system"
  ) {
    try {
      await this.auditLog.add({
        entityType,
        entityId,
        action,
        oldData: oldData ? JSON.stringify(oldData) : null,
        newData: newData ? JSON.stringify(newData) : null,
        userId,
        timestamp: new Date(),
        ipAddress: "localhost", // In a real app, this would be the actual IP
      });
    } catch (error) {
      console.error("Error logging audit event:", error);
    }
  }

  async getAuditTrail(entityType, entityId) {
    try {
      return await this.auditLog
        .where({ entityType, entityId })
        .orderBy("timestamp")
        .reverse()
        .toArray();
    } catch (error) {
      console.error("Error getting audit trail:", error);
      return [];
    }
  }

  async getAllAuditLogs(limit = 100) {
    try {
      return await this.auditLog
        .orderBy("timestamp")
        .reverse()
        .limit(limit)
        .toArray();
    } catch (error) {
      console.error("Error getting audit logs:", error);
      return [];
    }
  }

  // Enhanced transaction methods with audit logging
  async createTransactionWithAudit(transactionData, userId = "system") {
    try {
      const transaction = await this.createTransaction(transactionData);
      await this.logAuditEvent(
        "transaction",
        transaction.id,
        "CREATE",
        null,
        transaction,
        userId
      );
      return transaction;
    } catch (error) {
      console.error("Error creating transaction with audit:", error);
      throw error;
    }
  }

  async updateTransactionWithAudit(transactionId, updates, userId = "system") {
    try {
      const oldTransaction = await this.getTransaction(transactionId);
      const updatedTransaction = await this.updateTransaction(
        transactionId,
        updates
      );
      await this.logAuditEvent(
        "transaction",
        transactionId,
        "UPDATE",
        oldTransaction,
        updatedTransaction,
        userId
      );
      return updatedTransaction;
    } catch (error) {
      console.error("Error updating transaction with audit:", error);
      throw error;
    }
  }

  // Data integrity methods
  async validateTransactionIntegrity() {
    try {
      const transactions = await this.getAllTransactions();
      const issues = [];

      for (const transaction of transactions) {
        // Check if paid amount exceeds total amount
        if (transaction.paidAmount > transaction.totalAmount) {
          issues.push({
            type: "OVERPAYMENT",
            transactionId: transaction.id,
            message: `Paid amount (₪${transaction.paidAmount}) exceeds total amount (₪${transaction.totalAmount})`,
          });
        }

        // Check if patient exists
        const patient = await this.getPatient(transaction.patientId);
        if (!patient) {
          issues.push({
            type: "MISSING_PATIENT",
            transactionId: transaction.id,
            message: `Patient with ID ${transaction.patientId} not found`,
          });
        }

        // Check if visit exists (if visitId is provided)
        if (transaction.visitId) {
          const visit = await this.getVisit(transaction.visitId);
          if (!visit) {
            issues.push({
              type: "MISSING_VISIT",
              transactionId: transaction.id,
              message: `Visit with ID ${transaction.visitId} not found`,
            });
          }
        }
      }

      return {
        isValid: issues.length === 0,
        issues,
        totalTransactions: transactions.length,
        checkedAt: new Date(),
      };
    } catch (error) {
      console.error("Error validating transaction integrity:", error);
      return {
        isValid: false,
        issues: [{ type: "VALIDATION_ERROR", message: error.message }],
        totalTransactions: 0,
        checkedAt: new Date(),
      };
    }
  }

  async reconcilePayments(startDate, endDate) {
    try {
      const transactions = await this.transactions
        .where("date")
        .between(startDate, endDate)
        .toArray();

      const summary = {
        totalTransactions: transactions.length,
        totalAmount: 0,
        totalPaid: 0,
        totalOutstanding: 0,
        paymentMethods: {},
        statusBreakdown: {},
      };

      transactions.forEach((transaction) => {
        summary.totalAmount += transaction.totalAmount;
        summary.totalPaid += transaction.paidAmount;
        summary.totalOutstanding +=
          transaction.totalAmount - transaction.paidAmount;

        // Payment method breakdown
        const method = transaction.paymentMethod || "unknown";
        summary.paymentMethods[method] =
          (summary.paymentMethods[method] || 0) + transaction.paidAmount;

        // Status breakdown
        const status = transaction.status || "unknown";
        summary.statusBreakdown[status] =
          (summary.statusBreakdown[status] || 0) + 1;
      });

      return summary;
    } catch (error) {
      console.error("Error reconciling payments:", error);
      throw error;
    }
  }
}

// Initialize database instance
const medicalDB = new MedicalDatabase();
export default medicalDB;
